
import React from 'react';
import { Grid, Select, TextInput, ActionIcon } from '@mantine/core';
import { IconTrash } from '@tabler/icons-react';
import { Student } from '../types/batch.types';

interface StudentFormProps {
  student: Student;
  index: number;
  studentsDropdown: { label: string; value: string; name: string }[];
  onStudentChange: (index: number, field: keyof Student, value: any, isEdit?: boolean) => void;
  onEmailChange: (index: number, email: string | null, isEditMode?: boolean) => void;
  onRemove: (index: number) => void;
  editMode?: boolean;
  isEditBatch?: boolean;
}

export const StudentForm: React.FC<StudentFormProps> = ({
  student,
  index,
  studentsDropdown,
  onStudentChange,
  onEmailChange,
  onRemove,
  editMode = false,
  isEditBatch = false,
}) => {
  return (
    <Grid gutter="md" align="center" mb="md">
      <Grid.Col span={{ base: 12, sm: 6 }}>
        <Select
          label="Student Email"
          placeholder="Select Email"
          data={studentsDropdown}
          value={student.email}
          onChange={(email) => onEmailChange(index, email, isEditBatch)}
          searchable
          clearable
          disabled={!editMode}
        />
      </Grid.Col>

      <Grid.Col span={{ base: 12, sm: 6 }}>
        <TextInput
          label="Student Name"
          value={student.name}
          readOnly
        />
      </Grid.Col>

      <Grid.Col span={{ base: 12, sm: 6 }}>
        <TextInput
          label="Amount"
          type="text"
          inputMode="decimal"
          value={student.amount !== undefined && student.amount !== null ? String(student.amount) : ''}
          onChange={(e) => {
            const value = e.target.value;
            // Allow only numbers and decimal point
            if (value === '' || /^\d*\.?\d*$/.test(value)) {
              // Pass raw string to parent so typing decimals doesn't get interrupted.
              onStudentChange(index, 'amount', value, isEditBatch);
            }
          }}
          readOnly={!editMode}
        />
      </Grid.Col>

      <Grid.Col span={{ base: 12, sm: 6 }}>
        <TextInput
          label="Received"
          type="text"
          inputMode="decimal"
          value={student.received !== undefined && student.received !== null ? String(student.received) : ''}
          onChange={(e) => {
            const value = e.target.value;
            // Allow only numbers and decimal point
            if (value === '' || /^\d*\.?\d*$/.test(value)) {
              onStudentChange(index, 'received', value, isEditBatch);
            }
          }}
          readOnly={!editMode}
        />
      </Grid.Col>

      <Grid.Col span={{ base: 12, sm: 6 }}>
        <TextInput
          label="Balance"
          type="text"
          inputMode="decimal"
          value={
            student.balance !== undefined && student.balance !== null
              ? // show formatted decimal if number, otherwise empty
                (typeof student.balance === 'number' ? student.balance.toFixed(2) : String(student.balance))
              : ''
          }
          readOnly
        />
      </Grid.Col>

      <Grid.Col span={{ base: 12, sm: 6 }}>
        <TextInput
          label="Referral"
          type="text"
          inputMode="decimal"
          value={student.referral !== undefined && student.referral !== null ? String(student.referral) : ''}
          onChange={(e) => {
            const value = e.target.value;
            // Allow only numbers and decimal point
            if (value === '' || /^\d*\.?\d*$/.test(value)) {
              onStudentChange(index, 'referral', value, isEditBatch);
            }
          }}
          readOnly={!editMode}
        />
      </Grid.Col>

      <Grid.Col span={{ base: 12, sm: 6 }}>
        <Select
          label="Currency"
          data={["USD", "INR", "CAD", "GBP", "NAIRA", "EURO", "AUD","AED"]}
          value={student.currency}
          onChange={(value) => onStudentChange(index, 'currency', value || 'INR', isEditBatch)}
          readOnly={!editMode}
        />
      </Grid.Col>

      <Grid.Col span={{ base: 12, sm: 6 }}>
        <Select
          label="Payment Status"
          data={["Partially Paid", "Not Applicable", "Absconded", "Fully Paid"]}
          value={student.paymentStatus}
          onChange={(value) => onStudentChange(index, 'paymentStatus', value || 'Not Applicable', isEditBatch)}
          readOnly={!editMode}
        />
      </Grid.Col>

      <Grid.Col span={{ base: 12, sm: 6 }}>
        <TextInput
          label="Comment"
          value={student.comment}
          onChange={(e) => onStudentChange(index, 'comment', e.target.value, isEditBatch)}
          readOnly={!editMode}
        />
      </Grid.Col>
      <Grid.Col span={{ base: 12, sm: 6 }}>
        <TextInput
          label="Classes"
          value={student.studentClasses}
          onChange={(e) => onStudentChange(index, 'studentClasses', e.target.value, isEditBatch)}
          readOnly={!editMode}
        />
      </Grid.Col>

      {editMode && (
        <Grid.Col span={{ base: 12, sm: 2 }}>
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <ActionIcon
              color="red"
              onClick={() => onRemove(index)}
              title="Remove Student"
              variant="light"
              size="lg"
            >
              <IconTrash size={16} />
            </ActionIcon>
          </div>
        </Grid.Col>
      )}
    </Grid>
  );
};


